import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { routes } from 'client-config';
import { GroupType } from 'graphql-query-contracts';
import { useCreateCostOffsetCode } from 'react-hooks';
import { useChannelAdminContext } from 'hooks';
import { AdminPage, PageHeader } from 'components/layout';

import {
  CostOffsetCodeForm,
  type CostOffsetCodeFormValues,
} from './CostOffsetCodeForm';

type Props = {
  breadcrumbs?: {
    label: string;
    url?: string;
  }[];
  className?: string;
};

export function CreateCostOffsetCodeForm({ breadcrumbs, className }: Props) {
  const history = useHistory();
  const { channel } = useChannelAdminContext();
  const { t } = useTranslation();

  const channelSlug = channel?.slug || '';
  const productServicesLink = routes.channelAdminProductsServices.replace(
    ':id',
    channelSlug
  );

  const defaultBreadcrumbs = [
    {
      label: t('adp.productsServices.breadcrumb'),
      url: productServicesLink,
    },
    { label: t('adp.productsServices.createCostOffsetCode.breadcrumb') },
  ];

  const { createCostOffsetCode, loading } = useCreateCostOffsetCode();

  const [submitError, setSubmitError] = useState<
    { severity: 'warning' | 'error'; error: Error } | undefined
  >(undefined);

  const handleCreate = async (data: CostOffsetCodeFormValues) => {
    if (!channel?._id) {
      window.Toast.show(
        t('abp.productsServices.createCostOffsetCode.error'),
        'error'
      );

      return;
    }

    setSubmitError(undefined);

    try {
      await createCostOffsetCode({
        code: data.code.trim(),
        description: data.description?.trim()
          ? data.description.trim()
          : undefined,
        groupId: channel._id,
        groupType: GroupType.GroupTypeActivateChannel,
      });

      window.Toast.show(
        t('abp.productsServices.createCostOffsetCode.createSuccessful')
      );
      history.goBack();
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error(
              typeof err === 'string' ? err : 'An unexpected error occurred'
            );

      setSubmitError({ severity: 'warning', error });
    }
  };

  const handleCancel = () => {
    history.goBack();
  };

  return (
    <AdminPage className={className}>
      <PageHeader
        externalPadding={[0, 0]}
        header={t('abp.productsServices.createCostOffsetCode.heading')}
        headerLevel="h3"
        breadcrumbs={breadcrumbs ?? defaultBreadcrumbs}
      />
      <CostOffsetCodeForm
        error={submitError}
        disabled={loading}
        onSubmit={handleCreate}
        onCancel={handleCancel}
      />
    </AdminPage>
  );
}
